import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PersonalizedOpenerGenerator from '../components/PersonalizedOpenerGenerator';
import ServiceSelector from '../components/ServiceSelector';
import OpenerStyleSettings from '../components/OpenerStyleSettings';
import OpenerPresets from '../components/OpenerPresets';


import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiSettings, FiSliders } = FiIcons;

const Openers = ({ results, config, openerConfig, setOpenerConfig, scrapingOptions }) => {
  const [showSettings, setShowSettings] = useState(false);
  // Initialize selectedService from openerConfig if available, otherwise default to 'website'
  const [selectedService, setSelectedService] = useState(openerConfig?.serviceType || 'website');
  const [selectedSubService, setSelectedSubService] = useState(openerConfig?.subServiceType || null);
  const [showPresets, setShowPresets] = useState(false);

  // Use openerConfig from props if available, otherwise use local state
  const [localOpenerConfig, setLocalOpenerConfig] = useState(
    openerConfig || { style: 'casual', length: 'medium', focus: 'refresh', strategy: '5line' }
  );

  // Sync selectedService with openerConfig when it changes from parent
  React.useEffect(() => {
    if (openerConfig?.serviceType && openerConfig.serviceType !== selectedService) {
      setSelectedService(openerConfig.serviceType);
    }
    if (openerConfig?.subServiceType !== selectedSubService) {
      setSelectedSubService(openerConfig.subServiceType || null);
    }
  }, [openerConfig?.serviceType, openerConfig?.subServiceType]);

  // Update opener config when service changes
  React.useEffect(() => {
    // If using props for opener config, update through that
    if (openerConfig && setOpenerConfig) {
      setOpenerConfig({
        ...openerConfig,
        serviceType: selectedService,
        subServiceType: selectedSubService
      });
    } else {
      // Otherwise update local state
      setLocalOpenerConfig(prevConfig => ({
        ...prevConfig,
        serviceType: selectedService,
        subServiceType: selectedSubService
      }));
    }
  }, [selectedService, selectedSubService]);

  // Handle opener config updates
  const handleOpenerConfigUpdate = (newConfig) => {
    if (setOpenerConfig) {
      setOpenerConfig(newConfig);
    } else {
      setLocalOpenerConfig(newConfig);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      className="openers-page"
    >


      <div className="page-header">
        <div className="title-section">
          <h1 className="page-title">Value-First Personalized Openers</h1>
          <p className="page-description">
            Generate tailored, value-first conversation starters that provide immediate insights before mentioning your services
          </p>
        </div>
        <div className="header-info">
          <div className="info-card">
            <SafeIcon icon={FiMessageCircle} className="info-icon" />
            <div className="info-content">
              <div className="info-value">{results.length}</div>
              <div className="info-label">Available Openers</div>
            </div>
          </div>
          <button 
            className="settings-toggle" 
            onClick={() => setShowSettings(!showSettings)} 
            title="Opener Settings"
          >
            <SafeIcon icon={showSettings ? FiSliders : FiSettings} />
          </button>
        </div>
      </div>

      <ServiceSelector 
        selectedService={selectedService} 
        setSelectedService={setSelectedService} 
        selectedSubService={selectedSubService} 
        setSelectedSubService={setSelectedSubService}
      />

      {showSettings && (
        <div className="settings-panel">
          <OpenerStyleSettings 
            openerConfig={openerConfig || localOpenerConfig} 
            setOpenerConfig={handleOpenerConfigUpdate} 
            selectedService={selectedService} 
            selectedSubService={selectedSubService}
          />
        </div>
      )}

      <div className="generator-section">
        <PersonalizedOpenerGenerator 
          results={results} 
          config={config} 
          openerConfig={{
            ...(openerConfig || localOpenerConfig), 
            serviceType: selectedService, 
            subServiceType: selectedSubService,
            scrapingOptions: scrapingOptions
          }}
        />
      </div>

      <div className="presets-toggle">
        <button className="toggle-button" onClick={() => setShowPresets(!showPresets)}>
          {showPresets ? 'Hide Value-First Templates' : 'Show Value-First Templates'}
        </button>
      </div>

      {showPresets && <OpenerPresets />}

      <style jsx>{`
        .openers-page {
          padding: 1.5rem;
        }
        
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        
        .title-section {
          flex: 1;
        }
        
        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0;
        }
        
        .page-description {
          font-size: 0.875rem;
          color: var(--text-light);
          margin: 0.25rem 0 0 0;
        }
        
        .header-info {
          display: flex;
          gap: 1rem;
          align-items: center;
        }
        
        .info-card {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
        }
        
        .info-icon {
          width: 36px;
          height: 36px;
          background-color: #eff6ff;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--blue-color);
          font-size: 1.25rem;
        }
        
        .info-content {
          display: flex;
          flex-direction: column;
        }
        
        .info-value {
          font-size: 1.25rem;
          font-weight: 700;
          line-height: 1;
        }
        
        .info-label {
          font-size: 0.75rem;
          color: var(--text-light);
          margin-top: 0.25rem;
        }
        
        .settings-toggle {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          color: var(--text-light);
          transition: all 0.2s;
        }
        
        .settings-toggle:hover {
          background-color: #f9fafb;
          color: var(--primary-color);
        }
        
        .settings-panel {
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          margin-bottom: 1.5rem;
          padding: 1rem;
        }
        
        .generator-section {
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          margin-bottom: 1.5rem;
        }
        
        .presets-toggle {
          display: flex;
          justify-content: center;
          margin: 1rem 0;
        }
        
        .toggle-button {
          padding: 0.75rem 1.5rem;
          background-color: #f5f3ff;
          color: var(--primary-color);
          border: 1px solid #e0e7ff;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .toggle-button:hover {
          background-color: #e0e7ff;
        }
        
        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .header-info {
            width: 100%;
            justify-content: space-between;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Openers;